import type {
  ClaimTransferResponse,
  CreateMatchRequest,
  CreateMatchResponse,
  CreateTransferResponse,
  GameMap,
  JoinMatchRequest,
  JoinMatchResponse,
  MatchSummary,
} from '@seven-dawns/shared';

/** Error returned by the API, carrying the server's message for display. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export type JoinRequestStatus =
  | { status: 'pending' }
  | { status: 'approved'; playerId: string; playerToken: string; code?: string }
  | { status: 'rejected' };

interface RequestOptions {
  method?: 'GET' | 'POST' | 'DELETE';
  body?: unknown;
  token?: string;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (options.token) headers.Authorization = `Bearer ${options.token}`;

  let response: Response;
  try {
    response = await fetch(path, {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError('Could not reach the server. Check your connection and try again.', 0);
  }

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  const data: unknown = text ? safeJson(text) : undefined;
  if (!response.ok) {
    const message =
      typeof data === 'object' && data !== null && 'error' in data && typeof data.error === 'string'
        ? data.error
        : `Request failed (${response.status})`;
    throw new ApiError(message, response.status);
  }
  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/** The REST client's shape, so demo mode can provide an offline implementation. */
export type Api = typeof api;

const code = (c: string) => encodeURIComponent(c.trim().toUpperCase());

export const api = {
  createMatch: (body: CreateMatchRequest) =>
    request<CreateMatchResponse>('/api/matches', { method: 'POST', body }),

  getMatch: (matchCode: string, signal?: AbortSignal) =>
    request<MatchSummary>(`/api/matches/${code(matchCode)}`, { signal }),

  joinMatch: (matchCode: string, body: JoinMatchRequest) =>
    request<JoinMatchResponse>(`/api/matches/${code(matchCode)}/join`, { method: 'POST', body }),

  startMatch: (matchCode: string, token: string) =>
    request<{ ok: true }>(`/api/matches/${code(matchCode)}/start`, { method: 'POST', token, body: {} }),

  leaveMatch: (matchCode: string, token: string) =>
    request<{ ok: true }>(`/api/matches/${code(matchCode)}/leave`, { method: 'POST', token, body: {} }),

  getMap: (matchCode: string, token: string, signal?: AbortSignal) =>
    request<GameMap>(`/api/matches/${code(matchCode)}/map`, { token, signal }),

  joinRequestStatus: (requestId: string, requestToken: string, signal?: AbortSignal) =>
    request<JoinRequestStatus>(`/api/join-requests/${encodeURIComponent(requestId)}`, {
      token: requestToken,
      signal,
    }),

  /** One-time link moving this player's seat to another device. */
  createTransfer: (matchCode: string, token: string) =>
    request<CreateTransferResponse>(`/api/matches/${code(matchCode)}/transfer`, {
      method: 'POST',
      token,
      body: {},
    }),

  /** Host only: a one-time link for a teammate who lost their device. */
  reissueSeat: (matchCode: string, token: string, playerId: string) =>
    request<CreateTransferResponse>(
      `/api/matches/${code(matchCode)}/players/${encodeURIComponent(playerId)}/transfer`,
      { method: 'POST', token, body: {} },
    ),

  claimTransfer: (transferToken: string) =>
    request<ClaimTransferResponse>('/api/transfer/claim', { method: 'POST', body: { transferToken } }),

  pushPublicKey: () => request<{ publicKey: string }>('/api/push/public-key'),

  pushSubscribe: (token: string, subscription: PushSubscriptionJSON) =>
    request<{ ok: true }>('/api/push/subscribe', { method: 'POST', token, body: subscription }),

  pushUnsubscribe: (token: string) =>
    request<{ ok: true }>('/api/push/subscribe', { method: 'DELETE', token }),
};
